/* ===================================================================
   useCadencePreferences() + update mutation -- the client-configurable
   request-scheduling controls (Hopper, 2026-06-17).

   Read:   GET  /api/v1/cadence-preferences  (saved + effective + limits)
   Write:  PUT  /api/v1/cadence-preferences  (any subset + pause toggle)

   The Settings -> "Request scheduling" panel binds to these. Integer knobs
   are clamped server-side to Penn's compliance caps (max 3 follow-up touches,
   min spacing >= 3 days, sane initial delay) -- never rejected -- so the UI
   always shows the post-clamp `effective` truth after a save. `paused` is a
   plain kill-switch: when true the engine sends nothing.

   The read hook is disabled until a Bearer rr_ token exists (same pattern as
   the other data hooks) so it never auto-fires a 401 on first paint. After a
   save we invalidate the cadence query so the panel reflects the new state.
   =================================================================== */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  api,
  type CadencePreferencesPatch,
  type CadencePreferencesResponse,
  type CadencePreferencesWriteResponse,
} from '../lib/api';
import { useApiToken } from './useApiToken';
import { qk } from './queryKeys';

export function useCadencePreferences(
  params: { enabled?: boolean } = {},
): UseQueryResult<CadencePreferencesResponse, Error> {
  const hasToken = Boolean(useApiToken());
  return useQuery({
    queryKey: qk.cadencePreferences(),
    queryFn: ({ signal }) => api.getCadencePreferences(signal),
    enabled: hasToken && params.enabled !== false,
    staleTime: 60_000,
  });
}

export interface UseUpdateCadencePreferencesResult {
  save: (patch: CadencePreferencesPatch) => Promise<CadencePreferencesWriteResponse>;
  isPending: boolean;
  error: Error | null;
}

export function useUpdateCadencePreferences(): UseUpdateCadencePreferencesResult {
  const qc = useQueryClient();
  const m = useMutation({
    mutationFn: (patch: CadencePreferencesPatch) =>
      api.updateCadencePreferences(patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.cadencePreferences() });
    },
  });
  return {
    save: (patch) => m.mutateAsync(patch),
    isPending: m.isPending,
    error: m.error ?? null,
  };
}
